import { useState, useEffect } from 'react';
import {
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { API_ENDPOINTS } from '../../config/constants';
import { useFirebaseAuth } from '../../contexts/FirebaseAuthContext';

interface CommentFormProps {
  postId: string;
  onCommentSubmitted?: () => void;
}

interface CommentData {
  author_name: string;
  author_email: string;
  author_website: string;
  author_photo: string;
  content: string;
}

const CommentForm = ({ postId, onCommentSubmitted }: CommentFormProps) => {
  const {
    user,
    loading: authLoading,
    signInWithGoogle,
    logout,
  } = useFirebaseAuth();

  const [formData, setFormData] = useState<CommentData>({
    author_name: '',
    author_email: '',
    author_website: '',
    author_photo: '',
    content: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [showForm, setShowForm] = useState(true);

  // Update form data when user signs in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        author_name: user.displayName || '',
        author_email: user.email || '',
        author_photo: user.photoURL || '',
      }));
    }
  }, [user]);

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to sign in with Google. Please try again.',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!user) {
      setMessage({
        type: 'error',
        text: 'Please sign in with Google to comment',
      });
      return;
    }

    if (!formData.content.trim()) {
      setMessage({
        type: 'error',
        text: 'Please write a comment',
      });
      return;
    }

    if (formData.content.length < 10) {
      setMessage({
        type: 'error',
        text: 'Comment must be at least 10 characters long',
      });
      return;
    }

    if (formData.content.length > 2000) {
      setMessage({
        type: 'error',
        text: 'Comment must be less than 2000 characters',
      });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.comments}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          post_id: postId,
          author_name: formData.author_name,
          author_email: formData.author_email,
          author_website: formData.author_website,
          author_photo: formData.author_photo,
          content: formData.content,
          firebase_uid: user.uid,
        }),
      });

      const result = await response.json();

      if (result.success) {
        const isApproved = result.data?.status === 'approved';
        setMessage({
          type: 'success',
          text: isApproved
            ? 'Your comment has been posted!'
            : 'Thank you! Your comment has been submitted and will be reviewed before being published.',
        });

        // Reset content only
        setFormData(prev => ({
          ...prev,
          content: '',
        }));

        // Only hide form if pending moderation
        if (!isApproved) {
          setShowForm(false);
        }

        // Refresh comments list
        if (onCommentSubmitted) {
          onCommentSubmitted();
        }
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to submit comment. Please try again.',
        });
      }
    } catch (error) {
      console.error('Comment submission error:', error);
      setMessage({
        type: 'error',
        text: 'Failed to submit comment. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setShowForm(true);
    setMessage(null);
    setFormData(prev => ({
      ...prev,
      content: '',
    }));
  };

  if (authLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="animate-pulse flex items-center justify-center py-8">
          <div className="h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!showForm && message?.type === 'success') {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="text-center">
          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            Comment Submitted!
          </h3>
          <p className="text-slate-600 mb-4">{message.text}</p>
          <button
            onClick={resetForm}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Leave another comment
          </button>
        </div>
      </div>
    );
  }

  // Not signed in - show login prompt
  if (!user) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-xl font-semibold text-slate-900 mb-4">
          Leave a Comment
        </h3>

        <div className="text-center py-6">
          <div className="mb-4">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <p className="text-slate-600 mb-4">
              Sign in with your Google account to join the conversation
            </p>
          </div>

          <button
            onClick={handleGoogleSignIn}
            className="inline-flex items-center px-6 py-3 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 transition-colors font-medium text-slate-700"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  // Signed in - show comment form
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-slate-900">
          Leave a Comment
        </h3>

        {/* User info and sign out */}
        <div className="flex items-center gap-3">
          {user.photoURL && (
            <img
              src={user.photoURL}
              alt={user.displayName || 'User'}
              className="w-8 h-8 rounded-full"
            />
          )}
          <div className="text-sm">
            <p className="font-medium text-slate-900">{user.displayName}</p>
            <button
              onClick={logout}
              className="text-slate-500 hover:text-slate-700 text-xs"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 p-3 rounded-lg flex items-start space-x-2 ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'error' && (
            <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
          )}
          {message.type === 'success' && (
            <CheckCircleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
          )}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Comment Content */}
        <div>
          <label
            htmlFor="content"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            Your Comment
          </label>
          <textarea
            id="content"
            rows={4}
            value={formData.content}
            onChange={e =>
              setFormData(prev => ({ ...prev, content: e.target.value }))
            }
            placeholder="Share your thoughts..."
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            required
            disabled={isSubmitting}
          />
          <div className="flex justify-between mt-1">
            <p className="text-xs text-slate-500">
              {formData.content.length}/2000 characters
            </p>
            {formData.content.length < 10 && formData.content.length > 0 && (
              <p className="text-xs text-red-500">
                At least 10 characters required
              </p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end pt-2">
          <button
            type="submit"
            disabled={isSubmitting || formData.content.length < 10}
            className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                Post Comment
              </>
            )}
          </button>
        </div>
      </form>

      {/* Privacy Notice */}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <p className="text-xs text-slate-500 leading-relaxed">
          Your name and comment will be publicly visible once approved. Comments
          are moderated and may take some time to appear.
        </p>
      </div>
    </div>
  );
};

export default CommentForm;
